import React, { useMemo } from 'react';
import {
    DataGrid,
    DataGridHeader,
    DataGridRow,
    DataGridHeaderCell,
    DataGridCell,
    DataGridBody,
    Body1,
    Caption1,
    Spinner,
    Badge,
    createTableColumn,
    TableColumnDefinition,
    TableCellLayout,
    Tooltip,
} from '@fluentui/react-components';
import {
    Person16Regular,
    ChatMultiple16Regular,
    Star16Regular,
    Timer16Regular,
    HandRight16Regular
} from '@fluentui/react-icons';
import { GroundTeamUserRatingsSummaryDto } from '../../../model/brokerConnections/IBrokerConnectionGroundTeamRatings';
import { useGroundTeamRatingsSummaryStyles } from './GroundTeamRatingsSummaryList.styles';
import RatingStars from '../RatingStars/RatingStars';
import { useTranslation } from 'react-i18next';
import { LoadStatus } from '../../../model';
import { TFunction } from 'i18next';
import { formatElapsedMilliseconds } from '../../../utils/formatters';

interface GroundTeamUsersListProps {
    users: GroundTeamUserRatingsSummaryDto[];
    onSelectUser: (user: GroundTeamUserRatingsSummaryDto) => void;
    loadStatus: LoadStatus;
}

const getColumns = (
    t: TFunction,
    styles: ReturnType<typeof useGroundTeamRatingsSummaryStyles>
): TableColumnDefinition<GroundTeamUserRatingsSummaryDto>[] => {

    return [
        createTableColumn<GroundTeamUserRatingsSummaryDto>({
            columnId: 'displayName',
            renderHeaderCell: () => (
                <TableCellLayout media={<Person16Regular />}>
                    {t('TableHeader_Ratings_DisplayName')}
                </TableCellLayout>
            ),
            renderCell: (item: GroundTeamUserRatingsSummaryDto) => {
                const displayValue = item.displayName || `User ${item.userId?.substring(0, 8) || 'unknown'}`;
                return (
                    <TableCellLayout className={styles.nameCell}>
                        {displayValue}
                    </TableCellLayout>
                );
            },
            compare: (a, b) => {
                const aName = a.displayName || a.userId || '';
                const bName = b.displayName || b.userId || '';
                return aName.localeCompare(bName);
            },
        }),

        createTableColumn<GroundTeamUserRatingsSummaryDto>({
            columnId: 'totalConversationsOwned',
            renderHeaderCell: () => (
                <TableCellLayout media={<ChatMultiple16Regular />}>
                    {t('TableHeader_Ratings_TotalConversations')}
                </TableCellLayout>
            ),
            renderCell: (item: GroundTeamUserRatingsSummaryDto) => (
                <TableCellLayout>
                    {item.totalConversationsOwned}
                </TableCellLayout>
            ),
            compare: (a, b) => a.totalConversationsOwned - b.totalConversationsOwned,
        }),

        createTableColumn<GroundTeamUserRatingsSummaryDto>({
            columnId: 'averageRating',
            renderHeaderCell: () => (
                <TableCellLayout media={<Star16Regular />}>
                    {t('TableHeader_Ratings_AvgRating')}
                </TableCellLayout>
            ),
            renderCell: (item: GroundTeamUserRatingsSummaryDto) => (
                <TableCellLayout className={styles.ratingCell}>
                    <div className={styles.ratingWrapper}>
                        <RatingStars rating={item.averageRating} size="small" />
                        <span className={styles.ratingValue}>
                            {item.averageRating.toFixed(2)}
                        </span>
                        <Badge appearance="tint" size="small">
                            {item.totalRatedConversations} {item.totalRatedConversations === 1 ? 'rating' : 'ratings'}
                        </Badge>
                    </div>
                </TableCellLayout>
            ),
            compare: (a, b) => a.averageRating - b.averageRating,
        }),


        createTableColumn<GroundTeamUserRatingsSummaryDto>({
            columnId: 'averageFirstReplyTime',
            renderHeaderCell: () => (
                <TableCellLayout media={<Timer16Regular />}>
                    {t('TableHeader_Ratings_AvgFirstReply')}
                </TableCellLayout>
            ),
            renderCell: (item: GroundTeamUserRatingsSummaryDto) => {
                const seconds = item.averageFirstReplyTimeSeconds;

                if (seconds === null || seconds === undefined) {
                    return (
                        <TableCellLayout>
                            <Badge appearance="outline">No data</Badge>
                        </TableCellLayout>
                    );
                }

                return (
                    <TableCellLayout>
                        {formatElapsedMilliseconds(seconds * 1000)}
                    </TableCellLayout>
                );
            },
            compare: (a, b) => {
                if (a.averageFirstReplyTimeSeconds === null && b.averageFirstReplyTimeSeconds === null) return 0;
                if (a.averageFirstReplyTimeSeconds === null) return 1;
                if (b.averageFirstReplyTimeSeconds === null) return -1;
                return a.averageFirstReplyTimeSeconds - b.averageFirstReplyTimeSeconds;
            },
        }),
        createTableColumn<GroundTeamUserRatingsSummaryDto>({
            columnId: 'averageHandlingTime',
            renderHeaderCell: () => (
                <TableCellLayout media={<HandRight16Regular />}>
                    {t('TableHeader_Ratings_AvgHandling')}
                </TableCellLayout>
            ),
            renderCell: (item: GroundTeamUserRatingsSummaryDto) => {
                const seconds = item.averageHandlingTimeSeconds;

                if (seconds === null || seconds === undefined) {
                    return (
                        <TableCellLayout>
                            <Badge appearance="outline">No data</Badge>
                        </TableCellLayout>
                    );
                }

                return (
                    <TableCellLayout>
                        {formatElapsedMilliseconds(seconds * 1000)}
                    </TableCellLayout>
                );
            },
            compare: (a, b) => {
                if (a.averageHandlingTimeSeconds === null && b.averageHandlingTimeSeconds === null) return 0;
                if (a.averageHandlingTimeSeconds === null) return 1;
                if (b.averageHandlingTimeSeconds === null) return -1;
                return a.averageHandlingTimeSeconds - b.averageHandlingTimeSeconds;
            },
        }),


    ]
};

const columnSizingOptions = {
    displayName: {
        defaultWidth: 200,
        minWidth: 150,
    },
    averageRating: {
        defaultWidth: 280,
        minWidth: 240,
    },
    totalConversationsOwned: {
        defaultWidth: 120,
        minWidth: 100,
    },
    averageFirstReplyTime: {
        defaultWidth: 150,
        minWidth: 120,
    },
    averageHandlingTime: {
        defaultWidth: 150,
        minWidth: 120,
    },
};


const getHeaderCellClassName = (
    columnId: string,
    styles: ReturnType<typeof useGroundTeamRatingsSummaryStyles>
): string => {
    if (columnId.endsWith('Action')) {
        return styles.actionCell;
    }
    if (columnId === 'select') {
        return styles.tableSelectionCell;
    }
    return '';
};

const GroundTeamRatingsSummaryList: React.FC<GroundTeamUsersListProps> = ({ users, onSelectUser, loadStatus }) => {
    const styles = useGroundTeamRatingsSummaryStyles();
    const { t } = useTranslation();
    const columns = useMemo(() => getColumns(t, styles), [t, styles])
   
    if (loadStatus === 'loading' && users.length === 0) {
        return (
            <div className={styles.centerContent}>
                <Spinner label="Loading ground team ratings..." />
            </div>
        );
    }

    if (users.length === 0) {
        return (
            <div className={styles.emptyState}>
                <Body1>{t('PageHeader_Ratings')}</Body1>
                <Caption1>{t('Subtitle_Ratings_NoRatings')}</Caption1>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <DataGrid
                items={users}
                columns={columns}
                getRowId={(row) => row.userId}
                resizableColumns={true}
                sortable={true}
                columnSizingOptions={columnSizingOptions}
            >
                <DataGridHeader>
                    <DataGridRow className={styles.tableHeaderRow}>
                        {(column) => (
                            <DataGridHeaderCell
                                key={column.columnId}
                                className={getHeaderCellClassName(
                                    column.columnId.toString(),
                                    styles
                                )}
                            >
                                {column.renderHeaderCell()}
                            </DataGridHeaderCell>
                        )}
                    </DataGridRow>
                </DataGridHeader>
                <DataGridBody>
                    {(row) => (
                        <Tooltip
                            content={t('Tooltip_ViewUserDetails') || 'Click to view user details'}
                            relationship="label"
                            positioning="below"
                        >
                            <DataGridRow
                                key={row.rowId}
                                className={styles.tableBodyRow}
                                onClick={() => onSelectUser(row.item as GroundTeamUserRatingsSummaryDto)}
                                tabIndex={0}
                                role="button"
                                aria-label="View user rating details"
                            >
                                {(column) => (
                                    <DataGridCell key={column.columnId}>
                                        {column.renderCell(row.item as GroundTeamUserRatingsSummaryDto)}
                                    </DataGridCell>
                                )}
                            </DataGridRow>
                        </Tooltip>
                    )}
                </DataGridBody>
            </DataGrid>
        </div>
    );
};

export default GroundTeamRatingsSummaryList;